import clsx from 'clsx';
import { useState } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import { iconMap } from '../../navigation/icon-mapper';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '../combobox/combobox';
import type { IconItem, IconSelectorFieldProps } from './icon-selector-field.props';

const allIcons = Object.keys(iconMap)
  .map((key) => ({
    icon: key,
    iconFileName: iconMap[key],
  }))
  .sort((a, b) => a.iconFileName.localeCompare(b.iconFileName));

export function IconSelectorField({
  name,
  label,
  value,
  readOnly,
  disabled,
  required,
  error,
  onChange,
  className,
  placeholder = 'Search icons...',
}: IconSelectorFieldProps) {
  const [selectedValue, setSelectedValue] = useState<IconItem | null>(
    value ? { icon: value, iconFileName: iconMap[value] } : null
  );

  const handleValueChange = (nextValue: IconItem | null) => {
    setSelectedValue(nextValue);

    if (nextValue) {
      onChange?.(nextValue.icon);
    } else {
      onChange?.(undefined);
    }
  };

  if (readOnly) {
    return (
      <div className={className}>
        {label && (
          <div className="odin-flex">
            <FieldLabel label={label} name={name} />
            <RequiredIndicator readOnly={readOnly} required={required} />
          </div>
        )}
        {selectedValue && (
          <div className="odin-flex odin-items-center odin-gap-2 odin-border-none odin-bg-transparent">
            <OdinIcon icon={selectedValue.iconFileName} size={OdinIconSize.ExtraSmall} />
            <span className="odin-text-sm">{selectedValue.iconFileName}</span>
          </div>
        )}
        {error && <ErrorLabel>{error}</ErrorLabel>}
      </div>
    );
  }

  return (
    <>
      {label && (
        <div className="odin-flex">
          <FieldLabel label={label} name={name} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <Combobox
        items={allIcons}
        itemToStringLabel={(icon) => icon.iconFileName}
        value={selectedValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        isItemEqualToValue={isItemEqualToValue}
      >
        <ComboboxInput
          placeholder={placeholder}
          disabled={disabled}
          error={error !== undefined}
          inputClassName={clsx({ 'odin-pl-8 odin-pr-14': selectedValue, 'odin-pr-8': !selectedValue })}
          showClear
        >
          {selectedValue && (
            <div className="odin-absolute odin-left-0 odin-flex odin-items-center odin-justify-center odin-w-5 odin-mx-1">
              <OdinIcon icon={selectedValue.iconFileName} size={OdinIconSize.ExtraSmall} />
            </div>
          )}
        </ComboboxInput>
        <ComboboxContent>
          <ComboboxEmpty>No icons found</ComboboxEmpty>
          <ComboboxList>
            {(icon: IconItem) => (
              <ComboboxItem
                key={icon.icon}
                value={icon}
                showIndicator={false}
                className="odin-flex odin-items-center odin-gap-3 odin-px-3 odin-py-2 odin-leading-4"
              >
                <OdinIcon icon={icon.iconFileName} size={OdinIconSize.Small} />
                <span className="odin-flex odin-flex-col odin-text-sm">{icon.iconFileName}</span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}

const isItemEqualToValue = (item: IconItem, value: IconItem | null) => {
  return item.icon === value?.icon && item.iconFileName === value?.iconFileName;
};
